import { useShouldShowAgentsTab } from "@circle-react/components/SettingsApp/CommunityInbox/hooks/useShouldShowAgentsTab";
import { roundedCount } from "@circle-react/helpers/notificationHelpers";
import {
  useChatUIStore,
  useCommunityInboxStore,
} from "@circle-react/hooks/chatsV2/store";
import { useChatNotification } from "@circle-react/hooks/chatsV2/useChatNotification";
import { useShouldShowMessagingRoutes } from "@circle-react/hooks/useIsMessagingEnabled";
import type { Tab as CompassTab } from "@circleco/compass/components/Tabs";
import { useCallback, useEffect, useMemo } from "react";

import { t } from "@/i18n-js/instance";

import { TAB_AGENTS, TAB_INBOX, TAB_UNREAD } from "./constants";

export const useDirectMessagesTabs = () => {
  const { unreadChatRoomUuids = [] } = useChatNotification();
  const unreadChatRoomsCount = unreadChatRoomUuids.length;
  const {
    totalUnreadChatsCount: totalUnreadBotChatsCount,
    fetchTotalUnreadChatsCount,
  } = useCommunityInboxStore();

  const { showMessagesTab: shouldShowMessagesTab } =
    useShouldShowMessagingRoutes();
  const shouldShowAgentsTab = useShouldShowAgentsTab();
  const { dmSelectedTabIndex, setDmSelectedTabIndex } = useChatUIStore();

  useEffect(() => {
    if (shouldShowAgentsTab) {
      void fetchTotalUnreadChatsCount();
    }
  }, [shouldShowAgentsTab, fetchTotalUnreadChatsCount]);

  const tabs = useMemo(() => {
    const result: CompassTab[] = [];
    if (shouldShowMessagesTab) {
      result.push(
        {
          value: TAB_INBOX,
          label: t("messaging.inbox"),
        },
        {
          value: TAB_UNREAD,
          label: t("messaging.unread"),
          badge:
            unreadChatRoomsCount > 0
              ? String(roundedCount(unreadChatRoomsCount))
              : undefined,
        },
      );
    }
    if (shouldShowAgentsTab) {
      result.push({
        icon: "sparkles",
        value: TAB_AGENTS,
        label: t("messaging.agents"),
        badge:
          totalUnreadBotChatsCount > 0
            ? String(roundedCount(totalUnreadBotChatsCount))
            : undefined,
      });
    }
    return result;
  }, [
    shouldShowMessagesTab,
    shouldShowAgentsTab,
    unreadChatRoomsCount,
    totalUnreadBotChatsCount,
  ]);

  const tabValues = useMemo(() => tabs.map(tab => tab.value), [tabs]);
  const selectedValue = tabValues[dmSelectedTabIndex] ?? tabValues[0];

  const handleTabChange = useCallback(
    (value: string) => {
      const index = tabValues.indexOf(value);
      if (index !== -1) {
        setDmSelectedTabIndex(index);
      }
    },
    [tabValues, setDmSelectedTabIndex],
  );

  return {
    dmSelectedTabIndex,
    handleTabChange,
    selectedValue,
    setDmSelectedTabIndex,
    shouldShowAgentsTab,
    shouldShowMessagesTab,
    tabs,
    totalUnreadBotChatsCount,
    unreadChatRoomUuids,
    unreadChatRoomsCount,
  };
};
